import React, { memo } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import type { CarConfigDispatchType } from "../../../../../redux/store";
import { useCarConfigSelector } from "../../../../../redux/store";
import CarDataConsolidator from "../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { AvailableInOtherGradeModalType, CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import type { SectionWrapperPropagationProps } from "../SectionWrapper";
import { groupUpholsteriesBaseOnPack } from "../../../../../utils/packUtils";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import { SpinnerType, setActiveSpin } from "../../../../../redux/slices/CarConfigSpinNavigationSlice";
import { trackUpholsteryAddToBasket } from "../../../../../utils/tracking";
import { setUpholstery } from "../../../../../redux/slices/CarConfigBasketSlice";
import type { UpholsteryType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import UpholsterySkeleton from "./UpholsterySkeleton";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import SectionWrapper from "../SectionWrapper";
import baseSectionStyles from "../SectionWrapper.module.scss";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import SliderWrapper from "../../../common/spin/sliderWrapper/SliderWrapper";
import styles from "./Upholstery.module.scss";
import UpholsteryCard from "./UpholsteryCard";
import useAphModal from "../../../../../hooks/useAphModal";
import { MODAL_OTHER_GRADE } from "../../../../../utils/modalConstants";

const Upholstery: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }) => {
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const dispatch = useDispatch<CarConfigDispatchType>();
    const upholsteries = useCarConfigSelector(
        (state) =>
            CarDataConsolidator.getUpholsteries(
                state.items.upholsteries,
                state.items.equipmentUpholsteries,
                state.basket,
            ),
        shallowEqual,
    );
    const activeUpholstery = useCarConfigSelector((state) => state.basket.upholstery);
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const upholsteriesInOtherGrades = useCarConfigSelector(
        (state) => state.availableInOtherGrades.availableUpholsteries,
    );
    const itemsAreInitialized = useCarConfigSelector((state) => state.items.initialized);

    const upholsteriesInGroups = groupUpholsteriesBaseOnPack(upholsteries, packs);

    const carConfigBuyNow = useCarConfigLabel("carConfigAvailableInOtherGradesSummaryTitle");

    const { showModalHelper } = useAphModal();

    const navigationLabel = useCarConfigLabel("ariaUpholsteryNavigation");

    return (
        <SectionWrapper
            sectionId={CarConfigConfigureSection.Upholstery}
            sectionRef={sectionRefs.get(CarConfigConfigureSection.Upholstery)}
        >
            <div className={baseSectionStyles["group-wrapper"]}>
                <Text tag="h2" variant="apheleia-h2" className={styles.title}>
                    <CarConfigLabel label="carConfigUpholstery" />
                </Text>
                <SliderWrapper />
                <UpholsterySkeleton loading={!itemsAreInitialized}>
                    {Object.entries(upholsteriesInGroups).map(([groupPackId, upholsteriesInGroup]) => {
                        return (
                            <div
                                key={groupPackId}
                                className={groupPackId !== "standard" ? baseSectionStyles["pack-group-option"] : ""}
                            >
                                {groupPackId !== "standard" && (
                                    <div className={baseSectionStyles["pack-group-option-header"]}>
                                        <CarConfigLabel
                                            label="carConfigPartOfPack"
                                            params={{ pack: packs.find((pack) => pack.shortId === groupPackId)?.name }}
                                        />
                                        <Button
                                            className={baseSectionStyles["pack-group-button"]}
                                            variant="btn-text"
                                            onClick={() => scrollToTarget(CarConfigConfigureSection.Packs)}
                                        >
                                            <CarConfigLabel label="carConfigViewPack" />
                                        </Button>
                                    </div>
                                )}
                                <nav aria-label={navigationLabel}>
                                    <div className={styles.upholsteriesMobile}>
                                        <div className={styles.upholsteryItem}>
                                            <Text
                                                tag="h2"
                                                variant="apheleia-h4"
                                                className={styles.upholsteryItemTitle}
                                                data-testid="upholstery-name"
                                            >
                                                {activeUpholstery.name}
                                            </Text>
                                            {activeUpholstery.description && (
                                                <Text
                                                    tag="span"
                                                    variant="apheleia-body"
                                                    className={styles.upholsteryDescription}
                                                    dangerouslySetInnerHTML={{ __html: activeUpholstery.description }}
                                                />
                                            )}
                                            <div className={styles.upholsteryItemStatus}>
                                                <CarConfigLabel label="carConfigIncluded" />
                                            </div>
                                        </div>
                                        {upholsteriesInGroup.map((upholstery) => (
                                            <button
                                                type="button"
                                                key={upholstery.id}
                                                aria-pressed={activeUpholstery.id === upholstery.id}
                                                className={styles.upholsteryItemTile}
                                                onClick={() => {
                                                    dispatch(setActiveSpin(SpinnerType.Upholstery));
                                                    trackUpholsteryAddToBasket();
                                                    dispatch(
                                                        setUpholstery({ upholstery: upholstery as UpholsteryType }),
                                                    );
                                                }}
                                                data-testid="upholstery-item"
                                            >
                                                <div className={styles.upholsteryTileImage}>
                                                    <img
                                                        {...getCarDBAssetSrcSet(
                                                            commonSettings,
                                                            [
                                                                { width: 110, height: 110 },
                                                                { width: 215, height: 215 },
                                                                { width: 220, height: 220 },
                                                            ],
                                                            upholstery.image,
                                                        )}
                                                        width="110"
                                                        height="110"
                                                        sizes="(max-width: 767px) 110px, (max-width: 991px) 215px, 220px"
                                                        loading="lazy"
                                                        data-decorative="yes"
                                                        alt=""
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                                <span className={styles.upholsteryTileTitle}>{upholstery.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className={styles.upholsteriesDesktop}>
                                        {upholsteriesInGroup.map((upholstery, index) => (
                                            <UpholsteryCard
                                                upholstery={upholstery}
                                                active={upholstery.id === activeUpholstery.id}
                                                key={upholstery.id}
                                                style={{ "--or-animation-delay": `${(index + 1) * 100 + 4}` }}
                                                onClick={() => {
                                                    dispatch(setActiveSpin(SpinnerType.Upholstery));
                                                    trackUpholsteryAddToBasket();
                                                    dispatch(
                                                        setUpholstery({ upholstery: upholstery as UpholsteryType }),
                                                    );
                                                }}
                                            />
                                        ))}
                                    </div>
                                </nav>
                            </div>
                        );
                    })}
                </UpholsterySkeleton>

                {upholsteriesInOtherGrades.length > 0 && (
                    <Collapse summary={carConfigBuyNow} className={styles.other}>
                        {/* Disable because this is only a prevention and not an actual click for the user */}
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                        <div
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            className={styles.upholsteriesDesktop}
                        >
                            {upholsteriesInOtherGrades.map((upholstery, index) => (
                                <UpholsteryCard
                                    upholstery={upholstery}
                                    key={upholstery.id}
                                    style={{ "--or-animation-delay": `${(index + 1) * 100 + 4}` }}
                                    onClick={() => {
                                        showModalHelper(MODAL_OTHER_GRADE, {
                                            availableInOtherGradesProps: {
                                                section: AvailableInOtherGradeModalType.Upholstery,
                                                upholstery,
                                            },
                                        });
                                    }}
                                />
                            ))}
                        </div>
                        <div className={styles.upholsteriesMobile}>
                            {upholsteriesInOtherGrades.map((upholstery) => (
                                <button
                                    type="button"
                                    key={upholstery.id}
                                    aria-pressed={activeUpholstery.id === upholstery.id}
                                    className={styles.upholsteryItemTile}
                                    onClick={() => {
                                        showModalHelper(MODAL_OTHER_GRADE, {
                                            availableInOtherGradesProps: {
                                                section: AvailableInOtherGradeModalType.Upholstery,
                                                upholstery,
                                            },
                                        });
                                    }}
                                    data-testid="upholstery-item"
                                >
                                    <div className={styles.upholsteryTileImage}>
                                        <img
                                            {...getCarDBAssetSrcSet(
                                                commonSettings,
                                                [
                                                    { width: 110, height: 110 },
                                                    { width: 215, height: 215 },
                                                    { width: 220, height: 220 },
                                                ],
                                                upholstery.image,
                                            )}
                                            width="110"
                                            height="110"
                                            sizes="(max-width: 767px) 110px, (max-width: 991px) 215px, 220px"
                                            loading="lazy"
                                            data-decorative="yes"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <span className={styles.upholsteryTileTitle}>{upholstery.name}</span>
                                </button>
                            ))}
                        </div>
                    </Collapse>
                )}
            </div>
        </SectionWrapper>
    );
};

export default memo(Upholstery);
